// @flow
import { useEffect, useRef, useState } from 'react';
import './MyAccountAddress.css';
// Components
import { PageLoader, ConfirmationModal } from '../../../common';
import AddressAddNewButton from './MyAccountAddressAddNewButton';
import AddressEditor from './MyAccountAddressEditor';
import AddressItem from './MyAccountAddressItem';
// Types
import type { Address } from '../../types';

type State = {
  editingId: ?number, // ID of the address that is currently being edited
  isAdding: boolean, // Should the menu for adding a new address be open?
};

type Props = {
  addresses: Address[],
  onAddAddress: (address: Address) => void,
  onEditAddress: (id: number, address: Address) => void,
  onSubmitNew: (address: Address) => void,
  onSubmitUpdate: (address: Address) => void,
  onSubmitDelete: (addressId: number) => void,
  loading: boolean,
  editingLoading: boolean,
  addingLoading: boolean,
  deletingLoading: (id: number) => boolean,
};

const MyAccountAddress = (props: Props) => {
  const oldAddressLength = useRef(props?.addresses?.length ?? 0);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);

  const addressListLength = props?.addresses?.length ?? 0;

  const handleRemoveAddress = addressId => {
    setAddressToDelete(addressId);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (addressToDelete !== null) {
      props.onSubmitDelete(addressToDelete);
    }
    setIsConfirmModalOpen(false);
    setAddressToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsConfirmModalOpen(false);
    setAddressToDelete(null);
  };

  useEffect(() => {
    if (addressListLength > oldAddressLength.current) {
      setIsAdding(false);
    }

    if (props.editingLoading === false && editingId !== null) {
      setEditingId(null);
    }

    oldAddressLength.current = addressListLength;
  }, [addressListLength]);

  if (props.loading) {
    return <PageLoader />;
  }

  return (
    <div className="my-account-address-main">
      <div className="my-account-address-header">
        <h3>Meus endereços</h3>
        <AddressAddNewButton
          isEditing={isAdding}
          onClick={() => setIsAdding(!isAdding)}
        />
      </div>
      <div className="my-account-address-content-container">
        {isAdding ? (
          <AddressEditor
            address={null}
            onSubmitAddress={props.onSubmitNew}
            loading={props.addingLoading}
          />
        ) : (
          <div className="my-account-address-content">
            {props.addresses.map(address => {
              if (editingId === address.id) {
                return (
                  <div
                    key={address.id}
                    className="my-account-address-edit-item"
                  >
                    <AddressEditor
                      address={address}
                      loading={props.editingLoading}
                      onSubmitAddress={props.onSubmitUpdate}
                      isEditing={true}
                      onClickEdit={() => setEditingId(null)}
                    />
                  </div>
                );
              }

              return (
                <div>
                  <hr></hr>
                  <AddressItem
                    key={address.id}
                    address={address}
                    deletingLoading={props.deletingLoading(address.id)}
                    onClickEdit={() => setEditingId(address.id)}
                    onClickDelete={() => handleRemoveAddress(address.id)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        text="Deseja realmente deletar este endereço?"
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
      />
    </div>
  );
};

export default MyAccountAddress;
